import { useEffect } from "react";

/**
 * Portal lighting: carries over the marketing site's "Golden Core".
 * - Sets `data-portal-lit` on <html> so the card edge-catch CSS applies.
 * - Renders a persistent (always-on) warm-gold ambient glow behind content.
 * - Runs an IntersectionObserver that toggles `.in-light` on cards as they
 *   enter the central band of the viewport.
 */
export default function PortalLayout({ children }) {
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute("data-portal-lit", "true");

        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    e.target.classList.toggle("in-light", e.isIntersecting);
                }
            },
            { rootMargin: "-18% 0px -18% 0px", threshold: 0.01 },
        );
        const observeAll = () =>
            document
                .querySelectorAll(".tilt-card, [data-glow-catch]")
                .forEach((n) => io.observe(n));
        observeAll();
        const mo = new MutationObserver(observeAll);
        mo.observe(document.body, { childList: true, subtree: true });

        return () => {
            root.removeAttribute("data-portal-lit");
            io.disconnect();
            mo.disconnect();
        };
    }, []);

    return (
        <div className="min-h-screen relative text-[var(--text-primary)]">
            {/* Persistent ambient Golden Core */}
            <div
                aria-hidden="true"
                className="ambient-glow-layer"
                style={{
                    position: "fixed",
                    inset: 0,
                    pointerEvents: "none",
                    zIndex: 0,
                    background:
                        "radial-gradient(ellipse 1400px 900px at 50% 82%, rgba(255,220,138,0.13) 0%, rgba(212,175,55,0.07) 24%, rgba(139,10,10,0.05) 46%, rgba(0,0,0,0) 72%)",
                }}
            />
            <div className="relative" style={{ zIndex: 1 }}>
                {children}
            </div>
        </div>
    );
}
